"use client";

import { useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Posts pré-remplis du fil. Noms/pseudos littéraux, textes & horaires via i18n.
const SEED = [
    {
        id: 1,
        name: "Sarah Lopez",
        handle: "@sarahcodes",
        initials: "SL",
        grad: "from-sky-400 to-indigo-500",
        verified: false,
        timeKey: "Nextjs_threads_post1_time",
        textKey: "Nextjs_threads_post1_text",
        likes: 128,
        reposts: 24,
        replies: 12,
    },
    {
        id: 2,
        name: "Believemy",
        handle: "@believemy",
        initials: "B",
        grad: "from-violet-500 to-fuchsia-500",
        verified: true,
        timeKey: "Nextjs_threads_post2_time",
        textKey: "Nextjs_threads_post2_text",
        likes: 342,
        reposts: 88,
        replies: 41,
    },
    {
        id: 3,
        name: "Tom Mercier",
        handle: "@tombuild",
        initials: "TM",
        grad: "from-emerald-400 to-teal-500",
        verified: false,
        timeKey: "Nextjs_threads_post3_time",
        textKey: "Nextjs_threads_post3_text",
        likes: 76,
        reposts: 9,
        replies: 5,
    },
];

function Avatar({ initials, grad }) {
    return (
        <span
            className={`flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-gradient-to-br ${grad} text-white text-sm font-bold`}
        >
            {initials}
        </span>
    );
}

function Action({ icon, count, active, activeColor, onClick, label, reduce }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            className={`group inline-flex items-center gap-1.5 text-[13px] transition-colors ${
                active ? activeColor : "text-gray-500 hover:text-gray-200"
            }`}
        >
            <motion.span
                animate={reduce ? undefined : { scale: active ? [1, 1.3, 1] : 1 }}
                transition={{ duration: 0.3 }}
                className="inline-flex"
            >
                <SolarIcon name={icon} className="w-[18px] h-[18px]" />
            </motion.span>
            {count > 0 && <span className="tabular-nums">{count}</span>}
        </button>
    );
}

export default function NextjsThreadsDemo() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const taRef = useRef(null);
    const nextId = useRef(1000);

    const [text, setText] = useState("");
    const [posts, setPosts] = useState(() =>
        SEED.map((s) => ({
            ...s,
            text: t(s.textKey),
            time: t(s.timeKey),
            liked: false,
            reposted: false,
        })),
    );

    function publish() {
        const value = text.trim();
        if (!value) return;
        nextId.current += 1;
        setPosts((prev) => [
            {
                id: nextId.current,
                name: t("Nextjs_threads_you_name"),
                handle: t("Nextjs_threads_you_handle"),
                initials: t("Nextjs_threads_you_initials"),
                grad: "from-sky-400 to-indigo-500",
                verified: false,
                you: true,
                time: t("Nextjs_threads_now"),
                text: value,
                likes: 0,
                reposts: 0,
                replies: 0,
                liked: false,
                reposted: false,
            },
            ...prev,
        ]);
        setText("");
    }

    function toggleLike(id) {
        setPosts((prev) =>
            prev.map((p) =>
                p.id === id
                    ? {
                          ...p,
                          liked: !p.liked,
                          likes: p.likes + (p.liked ? -1 : 1),
                      }
                    : p,
            ),
        );
    }

    function toggleRepost(id) {
        setPosts((prev) =>
            prev.map((p) =>
                p.id === id
                    ? {
                          ...p,
                          reposted: !p.reposted,
                          reposts: p.reposts + (p.reposted ? -1 : 1),
                      }
                    : p,
            ),
        );
    }

    function replyTo(handle) {
        setText((prev) => {
            const mention = `${handle} `;
            return prev.startsWith(handle) ? prev : mention + prev;
        });
        taRef.current?.focus();
    }

    function onKeyDown(e) {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            publish();
        }
    }

    return (
        <section id="nextjs-threads" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Nextjs_threads_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Nextjs_threads_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <SolarIcon name="chat-round-bold" className="w-3.5 h-3.5" />
                        {t("Nextjs_threads_window_label")}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Nextjs_threads_badge")}
                        </span>
                    </span>
                </div>

                {/* Composer */}
                <div className="relative flex gap-3 px-5 py-4 border-b border-white/10 bg-white/[0.02]">
                    <Avatar
                        initials={t("Nextjs_threads_you_initials")}
                        grad="from-sky-400 to-indigo-500"
                    />
                    <div className="flex-1 min-w-0">
                        <textarea
                            ref={taRef}
                            value={text}
                            onChange={(e) =>
                                setText(e.target.value.slice(0, 280))
                            }
                            onKeyDown={onKeyDown}
                            rows={2}
                            placeholder={t("Nextjs_threads_composer_placeholder")}
                            className="w-full resize-none bg-transparent text-[15px] text-white placeholder:text-gray-500 outline-hidden leading-relaxed"
                        />
                        <div className="flex items-center justify-between mt-2">
                            <span className="text-[11px] font-mono text-gray-600 tabular-nums">
                                {text.length}/280
                            </span>
                            <button
                                type="button"
                                onClick={publish}
                                disabled={!text.trim()}
                                className="inline-flex items-center gap-1.5 rounded-full bg-sky-500 hover:bg-sky-400 disabled:bg-white/10 disabled:text-gray-500 transition-colors px-4 py-1.5 text-sm font-semibold text-white"
                            >
                                <SolarIcon
                                    name="plain-2-bold"
                                    className="w-4 h-4"
                                />
                                {t("Nextjs_threads_composer_button")}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Fil */}
                <div className="relative max-h-[460px] overflow-y-auto">
                    <AnimatePresence initial={false}>
                        {posts.map((p) => (
                            <motion.div
                                key={p.id}
                                layout={!reduce}
                                initial={
                                    reduce
                                        ? false
                                        : { opacity: 0, y: -12, scale: 0.98 }
                                }
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.35, ease: "easeOut" }}
                                className="flex gap-3 px-5 py-4 border-b border-white/5"
                            >
                                <Avatar initials={p.initials} grad={p.grad} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-sm font-semibold text-white">
                                            {p.name}
                                        </span>
                                        {p.verified && (
                                            <SolarIcon
                                                name="verified-check-bold"
                                                className="w-3.5 h-3.5 text-sky-400"
                                            />
                                        )}
                                        {p.you && (
                                            <span className="rounded-full bg-sky-500/15 ring-1 ring-sky-400/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-sky-300">
                                                {t("Nextjs_threads_you_badge")}
                                            </span>
                                        )}
                                        <span className="text-[13px] text-gray-500">
                                            {p.handle} · {p.time}
                                        </span>
                                    </div>
                                    <p className="text-[15px] text-gray-200 leading-relaxed mt-1 mb-0 whitespace-pre-wrap break-words">
                                        {p.text}
                                    </p>
                                    <div className="flex items-center gap-7 mt-3">
                                        <Action
                                            icon="chat-round-line-bold"
                                            count={p.replies}
                                            onClick={() => replyTo(p.handle)}
                                            label={t("Nextjs_threads_reply")}
                                            reduce={reduce}
                                        />
                                        <Action
                                            icon="refresh-bold"
                                            count={p.reposts}
                                            active={p.reposted}
                                            activeColor="text-emerald-400"
                                            onClick={() => toggleRepost(p.id)}
                                            label={t("Nextjs_threads_repost")}
                                            reduce={reduce}
                                        />
                                        <Action
                                            icon="heart-bold"
                                            count={p.likes}
                                            active={p.liked}
                                            activeColor="text-rose-500"
                                            onClick={() => toggleLike(p.id)}
                                            label={t("Nextjs_threads_like")}
                                            reduce={reduce}
                                        />
                                        <Action
                                            icon="square-share-line-bold"
                                            count={0}
                                            label={t("Nextjs_threads_share")}
                                            reduce={reduce}
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Nextjs_threads_caption")}
            </p>
        </section>
    );
}
